import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box,
    Text,
    Switch,
    VStack,
    HStack,
    Button,
    Tag,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    TableContainer,
    Spinner,
    Center,
    Tooltip,
    Icon,
    AlertDialog,
    AlertDialogOverlay,
    AlertDialogContent,
    AlertDialogHeader,
    AlertDialogBody,
    AlertDialogFooter,
    useColorModeValue,
    useToast,
} from '@chakra-ui/react';
import { LuLock, LuInfo } from 'react-icons/lu';
import { fetchWrapper } from '../../helpers';

/**
 * Known message types. `key` drives behaviour (which type is locked, which
 * one triggers the un-gating warning); `helper` is the row's explainer text.
 * Keyed by the id convention already used elsewhere in this app (see
 * MessageLog.js's getTypeId), with a name-based fallback below so a
 * differently-ordered id scheme still gets the right copy and behaviour.
 */
const MESSAGE_TYPE_META = {
    1: {
        key: 'authentication',
        helper: 'Security codes. Delivered regardless of consent.',
    },
    2: {
        key: 'transactional',
        helper: "Order, payment, and account activity. Gate only if this customer's policy requires it.",
    },
    3: {
        key: 'marketing',
        helper: 'Promotions and campaigns. Consent is normally required.',
    },
    4: {
        key: 'informational',
        helper: 'Announcements and updates.',
    },
};

const META_BY_NAME = Object.values(MESSAGE_TYPE_META).reduce((acc, meta) => {
    acc[meta.key] = meta;
    return acc;
}, {});

const getTypeMeta = (type) => {
    if (!type) return null;
    return (
        MESSAGE_TYPE_META[Number(type.id)] ||
        META_BY_NAME[String(type.name || '').trim().toLowerCase()] ||
        null
    );
};

// Authentication/OTP can't be gated by consent — it's always delivered, so its
// switch is locked off and ignores any saved value, bulk action, or click.
const isLocked = (type) => getTypeMeta(type)?.key === 'authentication';
const isMarketing = (type) => getTypeMeta(type)?.key === 'marketing';

const joinNames = (list) => {
    if (list.length === 0) return '';
    if (list.length === 1) return list[0];
    if (list.length === 2) return `${list[0]} and ${list[1]}`;
    return `${list.slice(0, -1).join(', ')}, and ${list[list.length - 1]}`;
};

/**
 * Consent Scope: which message types this customer's contact-level channel
 * consent gates.
 *
 * Channel consent itself is collected from the contact elsewhere — this page
 * only decides, per message type, whether that consent controls delivery.
 * A type switched ON is gated: it's delivered only to contacts who've
 * consented. A type switched OFF is not gated: it's delivered regardless of
 * consent status. `customer.opt_in_out` (from GET /customer/by-id) is `null`
 * until the customer has been configured — every type defaults to not gated
 * so nothing is silently gated behind a consent check before a first save.
 *
 * Choices are held locally and pushed in a single POST, so they land
 * atomically instead of one request per switch:
 *
 *   POST /customer/opt-in-out
 *   { customer_id, opt_in_out: [{ message_type_id, enable }] }
 *
 * `enable: true` means gated (consent required); `enable: false` means not
 * gated (always delivered).
 */
const OptInOut = ({ id, customer }) => {
    const [messageTypes, setMessageTypes] = useState([]);
    const [gated, setGated] = useState({});
    const [initial, setInitial] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);

    const toast = useToast();
    const textColor = useColorModeValue('gray.600', 'gray.300');
    const cancelRef = useRef(null);

    const customerId = Number(id ?? customer?.id);
    const savedOptInOut = customer?.opt_in_out;

    const loadMessageTypes = useCallback(async () => {
        setLoading(true);
        try {
            const typeRes = await fetchWrapper.get(
                `${process.env.REACT_APP_API_URL}/message-type/list`
            );
            const types = (typeRes?.data || []).filter(t => t?.id);
            const saved = Array.isArray(savedOptInOut) ? savedOptInOut : [];

            const state = {};
            types.forEach(t => {
                if (isLocked(t)) {
                    state[t.id] = false;
                    return;
                }
                const row = saved.find(s => Number(s.message_type_id) === Number(t.id));
                state[t.id] = row ? Boolean(row.enable) : false;
            });

            setMessageTypes(types);
            setGated(state);
            setInitial(state);
        } catch (error) {
            console.error('Error loading message types:', error);
            toast({
                title: 'Error',
                description: 'Failed to load message types',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [savedOptInOut]);

    useEffect(() => {
        loadMessageTypes();
    }, [loadMessageTypes]);

    const toggle = (type) => {
        if (isLocked(type)) return;
        setGated(prev => ({ ...prev, [type.id]: !prev[type.id] }));
    };

    const setAll = (value) =>
        setGated(messageTypes.reduce(
            (acc, t) => ({ ...acc, [t.id]: isLocked(t) ? false : value }), {}
        ));

    // Locked types can never be gated, so they're excluded from the "consent
    // enforced on X of Y" count and the bulk actions — otherwise "enforce on
    // all" could never reach 100% and the count would look permanently short.
    const optableTypes = messageTypes.filter(t => !isLocked(t));
    const gatedCount = optableTypes.filter(t => gated[t.id]).length;
    const isDirty = messageTypes.some(t => gated[t.id] !== initial[t.id]);

    const marketingType = messageTypes.find(isMarketing);
    const isUngatingMarketing = marketingType
        ? initial[marketingType.id] === true && gated[marketingType.id] === false
        : false;

    const runSave = async () => {
        setSaving(true);
        try {
            const payload = {
                customer_id: customerId,
                opt_in_out: messageTypes.map(t => ({
                    message_type_id: t.id,
                    enable: isLocked(t) ? false : Boolean(gated[t.id]),
                })),
            };
            const res = await fetchWrapper.post(
                `${process.env.REACT_APP_API_URL}/customer/opt-in-out`,
                payload
            );
            // fetchWrapper resolves with the raw Response on a non-2xx status,
            // so a rejected save would otherwise look like success.
            if (res instanceof Response) {
                throw new Error(`Request failed (${res.status})`);
            }

            setInitial(gated);
            toast({
                title: 'Success',
                description: 'Consent scope saved successfully',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
        } catch (error) {
            console.error('Error saving consent scope:', error);
            toast({
                title: 'Error',
                description: 'Failed to save consent scope',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setSaving(false);
        }
    };

    const handleSaveClick = () => {
        if (isUngatingMarketing) {
            setConfirmOpen(true);
            return;
        }
        runSave();
    };

    const handleConfirmSave = () => {
        setConfirmOpen(false);
        runSave();
    };

    if (loading) {
        return (
            <Center h="200px">
                <Spinner size="lg" color="blue.500" thickness="3px" />
            </Center>
        );
    }

    const gatedNames = messageTypes.filter(t => gated[t.id]).map(t => t.name);
    const notGatedNames = messageTypes.filter(t => !gated[t.id]).map(t => t.name);

    const previewSentence = messageTypes.length === 0 ? '' : gatedNames.length === 0
        ? `No message types are gated by consent — ${joinNames(notGatedNames)} ${notGatedNames.length === 1 ? 'is' : 'are'} sent to everyone regardless of consent status.`
        : notGatedNames.length === 0
            ? `Contacts on this account receive ${joinNames(gatedNames)} only if they've consented on the channel.`
            : `Contacts on this account receive ${joinNames(gatedNames)} only if they've consented on the channel; ${joinNames(notGatedNames)} ${notGatedNames.length === 1 ? 'is' : 'are'} sent to everyone.`;

    return (
        <Box>
            <VStack spacing={3} align="stretch">
                {/* Header */}
                <Box mb={1}>
                    <Text fontSize="sm" fontWeight="bold" mb={1}>
                        Consent Scope
                    </Text>
                    <Text color={textColor} fontSize="xs" maxWidth="720px">
                        Channel consent is captured from the contact. Choose which message types
                        that consent controls for this customer. Types left off are delivered
                        regardless of the contact's consent status. Authentication messages are
                        always delivered and can't be gated.
                    </Text>
                </Box>

                {messageTypes.length === 0 ? (
                    <Text fontSize="sm" color={textColor}>
                        No message types found.
                    </Text>
                ) : (
                    <>
                        <HStack justify="space-between" align="center" maxWidth="760px">
                            <Tag
                                colorScheme={gatedCount === optableTypes.length ? 'green' : 'blue'}
                                fontSize="xs"
                            >
                                Consent enforced on {gatedCount} of {optableTypes.length} types
                            </Tag>
                            <HStack spacing={2}>
                                <Button
                                    size="xs"
                                    variant="outline"
                                    onClick={() => setAll(true)}
                                    isDisabled={saving || gatedCount === optableTypes.length}
                                >
                                    Enforce consent on all
                                </Button>
                                <Button
                                    size="xs"
                                    variant="outline"
                                    onClick={() => setAll(false)}
                                    isDisabled={saving || gatedCount === 0}
                                >
                                    Remove consent gating
                                </Button>
                            </HStack>
                        </HStack>

                        <div className="table_section">
                            <TableContainer maxWidth="760px" border="1px solid" borderColor="gray.100" borderRadius="md">
                                <Table variant="simple" width="100%" sx={{ tableLayout: 'fixed' }}>
                                    <Thead>
                                        <Tr>
                                            <Th width="50%">Message Type</Th>
                                            <Th width="30%">Status</Th>
                                            <Th width="20%" textAlign="center">Consent Enforced</Th>
                                        </Tr>
                                    </Thead>
                                    <Tbody fontSize="14px">
                                        {messageTypes.map((type) => {
                                            const isOn = Boolean(gated[type.id]);
                                            const locked = isLocked(type);
                                            const helper = getTypeMeta(type)?.helper;
                                            return (
                                                <Tr key={type.id}>
                                                    <Td fontWeight="500" color="#111827" whiteSpace="normal">
                                                        <VStack align="start" spacing={0.5} py={1}>
                                                            <Text as="span" fontWeight="600">
                                                                {type.name}
                                                            </Text>
                                                            {helper && (
                                                                <Text fontSize="xs" color={textColor} fontWeight="400">
                                                                    {helper}
                                                                </Text>
                                                            )}
                                                        </VStack>
                                                    </Td>
                                                    <Td>
                                                        {locked ? (
                                                            <Tooltip label="Authentication messages are always delivered and can't be gated">
                                                                <Tag colorScheme="purple" fontSize="xs">
                                                                    <Icon as={LuLock} boxSize="10px" mr="1" />
                                                                    Always delivered
                                                                </Tag>
                                                            </Tooltip>
                                                        ) : (
                                                            <Tag
                                                                colorScheme={isOn ? 'green' : 'gray'}
                                                                fontSize="xs"
                                                            >
                                                                {isOn ? 'Consent required' : 'Not gated'}
                                                            </Tag>
                                                        )}
                                                    </Td>
                                                    <Td textAlign="center">
                                                        <Tooltip
                                                            label={
                                                                locked
                                                                    ? "Authentication messages are always delivered and can't be gated"
                                                                    : ''
                                                            }
                                                            isDisabled={!locked}
                                                        >
                                                            <Box display="inline-block">
                                                                <Switch
                                                                    colorScheme="blue"
                                                                    isChecked={isOn}
                                                                    isDisabled={saving || locked}
                                                                    onChange={() => toggle(type)}
                                                                    aria-label={`Toggle consent gating for ${type.name}`}
                                                                />
                                                            </Box>
                                                        </Tooltip>
                                                    </Td>
                                                </Tr>
                                            );
                                        })}
                                    </Tbody>
                                </Table>
                            </TableContainer>
                        </div>

                        {/* Live preview of what this configuration actually does */}
                        {previewSentence && (
                            <HStack
                                align="start"
                                spacing={2}
                                maxWidth="760px"
                                p={3}
                                bg="blue.50"
                                borderRadius="md"
                            >
                                <Icon as={LuInfo} color="blue.500" boxSize="14px" mt="2px" flexShrink={0} />
                                <Text fontSize="xs" color="blue.800">
                                    {previewSentence}
                                </Text>
                            </HStack>
                        )}

                        {/* Action Buttons */}
                        <HStack spacing={4} pt={2}>
                            <Button
                                colorScheme="blue"
                                size="sm"
                                px={6}
                                onClick={handleSaveClick}
                                isLoading={saving}
                                loadingText="Saving..."
                                isDisabled={!isDirty}
                            >
                                Save Settings
                            </Button>
                            {isDirty && (
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => setGated(initial)}
                                    isDisabled={saving}
                                >
                                    Discard Changes
                                </Button>
                            )}
                        </HStack>
                    </>
                )}
            </VStack>

            <AlertDialog
                isOpen={confirmOpen}
                leastDestructiveRef={cancelRef}
                onClose={() => setConfirmOpen(false)}
            >
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize="md" fontWeight="bold">
                            Remove Marketing Consent Gating?
                        </AlertDialogHeader>
                        <AlertDialogBody fontSize="sm">
                            Marketing messages will be delivered to contacts who have not given
                            consent. Confirm this matches the customer's agreement.
                        </AlertDialogBody>
                        <AlertDialogFooter>
                            <Button ref={cancelRef} size="sm" onClick={() => setConfirmOpen(false)}>
                                Cancel
                            </Button>
                            <Button
                                colorScheme="orange"
                                size="sm"
                                onClick={handleConfirmSave}
                                isLoading={saving}
                                ml={3}
                            >
                                Confirm &amp; Save
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </Box>
    );
};

export default OptInOut;
